// Le calendrier de conservation — combien de temps chaque categorie de renseignements se garde,
// par province, et pourquoi. GS-Admin en est le SEUL ecrivain (ADR-091 regle 7).
//
// Cadrage : docs/prompts/MASTER-FEAT-LE-CALENDRIER-DE-CONSERVATION-DANS-L-ADMINISTRATION-2026-10-07.md,
// demande 2. Le serveur : server/routes/calendrierConservation.ts.
//
// LES REGLES D AFFICHAGE :
//   - une duree vide se dit « sans terme fixe, a fixer par le juriste », jamais 0 ni « — » :
//     la ligne autorise la collecte et ne detruit jamais ;
//   - chaque ecriture porte sa date d effet et sa raison : elle devient un evenement date du
//     journal, que le tiroir « Historique » relit (ADR-071 R1) ;
//   - la page ne pose que des lignes redigees. Passer une ligne a « validee » est le geste du
//     juriste (hors de cette page) ; modifier une ligne validee la remet a redigee, et la page
//     le dit avant l enregistrement.
//
// @gs/ui tel quel (PageHeader, DataTable, GsDrawer, StatusBadge, QueryError, GsSelect, GsToggle).
import { useMemo, useState, type ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { AlertTriangle, CalendarClock, Download, FileText, History, Info, Pencil, Plus, RotateCcw, Shield, Trash2, X } from 'lucide-react';
import { ApiForbiddenError } from '@gs/api-client';
import {
  DataTable,
  GS_COMPONENTS,
  GsDrawer,
  GsSelect,
  GsToggle,
  PageHeader,
  QueryError,
  StatusBadge,
  useToast,
  type Column,
} from '@gs/ui';
import { useTranslation } from '../i18n';
import {
  useBasculerLigne,
  useCalendrierConservation,
  useChangementsDuCalendrier,
  useCreerLigne,
  useModifierLigne,
  usePrechargerLeCalendrierRedige,
  type ChangementDuCalendrier,
  type EtatDuCalendrierRedige,
  type FinDeConservation,
  type LigneDuCalendrier,
  type NatureDeSource,
  type SourceCitee,
  type VocabulaireDuCalendrier,
} from '../hooks/useCalendrierConservation';
import { codeDuRefus, dureeLisible, libelleDeClasse, libelleDeDeclencheur, libelleDeRefus } from '../lib/libellesDuCalendrier';

const champ = GS_COMPONENTS.input;
const etiquette = 'mb-1 block text-meta font-semibold text-slate-600 dark:text-slate-300';

/** Aujourd hui, en date pleine AAAA-MM-JJ, dans le fuseau du poste. */
function aujourdhui(): string {
  return new Date().toLocaleDateString('en-CA');
}

function dateLisible(iso: string, langue: string): string {
  // Une date pleine se lit a midi UTC : aucun fuseau ne la fait glisser d un jour.
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso);
  return new Intl.DateTimeFormat(langue, { dateStyle: 'medium' }).format(d);
}

function nomDeJuridiction(code: string, voc: VocabulaireDuCalendrier, langue: string): string {
  const j = voc.jurisdictions.find((x) => x.code === code);
  if (!j) return code;
  return langue.startsWith('en') ? j.nameEn : j.nameFr;
}

function nomDeRegime(code: string, voc: VocabulaireDuCalendrier, langue: string): string {
  const r = voc.regimes.find((x) => x.code === code);
  if (!r) return code;
  return langue.startsWith('en') ? r.nameEn : r.nameFr;
}

function libelleDeFin(fin: FinDeConservation, t: TFunction): string {
  return fin === 'anonymiser' ? t('admin:calendrier.fin.anonymiser') : t('admin:calendrier.fin.detruire');
}

function libelleDeNature(n: NatureDeSource, t: TFunction): string {
  switch (n) {
    case 'officielle': return t('admin:calendrier.nature.officielle');
    case 'guide': return t('admin:calendrier.nature.guide');
    default: return t('admin:calendrier.nature.recherche');
  }
}

function libelleDeChangement(genre: ChangementDuCalendrier['changeKind'], t: TFunction): string {
  switch (genre) {
    case 'creation': return t('admin:calendrier.historique.creation');
    case 'modification': return t('admin:calendrier.historique.modification');
    case 'validation': return t('admin:calendrier.historique.validation');
    case 'retrait': return t('admin:calendrier.historique.retrait');
    default: return t('admin:calendrier.historique.reprise');
  }
}

function Duree({ mois, langue, t }: { mois: number | null; langue: string; t: TFunction }) {
  if (mois === null) {
    return <span className="text-amber-700 dark:text-amber-400">{t('admin:calendrier.sansTerme')}</span>;
  }
  return <span className="font-semibold text-slate-800 dark:text-slate-100">{dureeLisible(mois, langue)}</span>;
}

function Statut({ ligne, t }: { ligne: LigneDuCalendrier; t: TFunction }) {
  if (!ligne.isActive) return <StatusBadge variant="neutral">{t('admin:calendrier.statut.retiree')}</StatusBadge>;
  return ligne.status === 'validee' ? (
    <StatusBadge variant="success">{t('admin:calendrier.statut.validee')}</StatusBadge>
  ) : (
    <StatusBadge variant="warning">{t('admin:calendrier.statut.redigee')}</StatusBadge>
  );
}

// ---------------------------------------------------------------------------
// Le formulaire : creation (avec la cle de la ligne) ou modification (la regle seule)
// ---------------------------------------------------------------------------

interface SourceSaisie {
  title: string;
  url: string;
  consultedOn: string;
  nature: NatureDeSource;
  draftedBefore2025: boolean;
}

interface Saisie {
  categoryCode: string;
  jurisdictionCode: string;
  regimeCode: string;
  sansTerme: boolean;
  durationMonths: string;
  triggerEvent: string;
  endAction: FinDeConservation;
  legalBasis: string;
  rationale: string;
  sources: SourceSaisie[];
  effectiveDate: string;
  reason: string;
}

const SOURCE_VIDE = (): SourceSaisie => ({
  title: '',
  url: '',
  consultedOn: aujourdhui(),
  nature: 'officielle',
  draftedBefore2025: false,
});

function saisieDe(ligne: LigneDuCalendrier | null): Saisie {
  if (!ligne) {
    return {
      categoryCode: '',
      jurisdictionCode: '',
      regimeCode: '',
      sansTerme: false,
      durationMonths: '',
      triggerEvent: '',
      endAction: 'detruire',
      legalBasis: '',
      rationale: '',
      sources: [SOURCE_VIDE()],
      effectiveDate: aujourdhui(),
      reason: '',
    };
  }
  return {
    categoryCode: ligne.categoryCode,
    jurisdictionCode: ligne.jurisdictionCode,
    regimeCode: ligne.regimeCode ?? '',
    sansTerme: ligne.durationMonths === null,
    durationMonths: ligne.durationMonths === null ? '' : String(ligne.durationMonths),
    triggerEvent: ligne.triggerEvent,
    endAction: ligne.endAction,
    legalBasis: ligne.legalBasis ?? '',
    rationale: ligne.rationale ?? '',
    sources: ligne.sources.map((s) => ({ ...s, url: s.url ?? '' })),
    effectiveDate: aujourdhui(),
    reason: '',
  };
}

/** Ce que la saisie a d incomplet, dit a l usager avant tout appel ; null = complete. */
function manqueDeLaSaisie(s: Saisie, creation: boolean, t: TFunction): string | null {
  if (creation && (!s.categoryCode || !s.jurisdictionCode)) return t('admin:calendrier.form.manqueCle');
  if (!s.sansTerme) {
    const n = Number(s.durationMonths);
    if (!Number.isInteger(n) || n < 1 || n > 1200) return t('admin:calendrier.form.manqueDuree');
  }
  if (!s.triggerEvent) return t('admin:calendrier.form.manqueDeclencheur');
  if (s.sources.length === 0 || s.sources.some((x) => !x.title.trim() || !x.consultedOn)) {
    return t('admin:calendrier.form.manqueSource');
  }
  if (!s.effectiveDate) return t('admin:calendrier.form.manqueDate');
  if (!s.reason.trim()) return t('admin:calendrier.form.manqueRaison');
  return null;
}

function sourcesEnvoyees(s: Saisie): SourceCitee[] {
  return s.sources.map((x) => ({
    title: x.title.trim(),
    url: x.url.trim() === '' ? null : x.url.trim(),
    consultedOn: x.consultedOn,
    nature: x.nature,
    draftedBefore2025: x.draftedBefore2025,
  }));
}

function regleEnvoyee(s: Saisie) {
  return {
    durationMonths: s.sansTerme ? null : Number(s.durationMonths),
    triggerEvent: s.triggerEvent,
    endAction: s.endAction,
    legalBasis: s.legalBasis.trim() === '' ? null : s.legalBasis.trim(),
    sources: sourcesEnvoyees(s),
    rationale: s.rationale.trim() === '' ? null : s.rationale.trim(),
    effectiveDate: s.effectiveDate,
    reason: s.reason.trim(),
  };
}

/** GsToggle n a pas de libelle visible : il est pose a cote, et dit aussi au lecteur d ecran. */
function Interrupteur({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <div className="flex items-center gap-2">
      <GsToggle checked={checked} onCheckedChange={onChange} aria-label={label} />
      <span className="text-field text-slate-700 dark:text-slate-200">{label}</span>
    </div>
  );
}

function Bandeau({ ton, icone: Icone, children }: { ton: 'ambre' | 'bleu'; icone: typeof Info; children: ReactNode }) {
  const couleurs =
    ton === 'ambre'
      ? 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200'
      : 'border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-200';
  return (
    <div className={`flex gap-2 rounded-xl border p-3 text-field ${couleurs}`}>
      <Icone size={18} className="mt-0.5 shrink-0" aria-hidden />
      <div>{children}</div>
    </div>
  );
}

function Formulaire({
  ligne,
  voc,
  onFermer,
}: {
  ligne: LigneDuCalendrier | null;
  voc: VocabulaireDuCalendrier;
  onFermer: () => void;
}) {
  const { t, i18n } = useTranslation();
  const langue = i18n.language;
  const { toast } = useToast();
  const creation = ligne === null;
  const [s, setS] = useState<Saisie>(() => saisieDe(ligne));
  const [manque, setManque] = useState<string | null>(null);
  const creer = useCreerLigne();
  const modifier = useModifierLigne();
  const enCours = creer.isPending || modifier.isPending;
  const refus = creer.error ?? modifier.error;

  const maj = <K extends keyof Saisie>(cle: K, valeur: Saisie[K]) => setS((p) => ({ ...p, [cle]: valeur }));
  const majSource = (i: number, patch: Partial<SourceSaisie>) =>
    setS((p) => ({ ...p, sources: p.sources.map((x, j) => (j === i ? { ...x, ...patch } : x)) }));

  const regimes = voc.regimes.filter((r) => r.jurisdictionCode === s.jurisdictionCode);

  const enregistrer = () => {
    const m = manqueDeLaSaisie(s, creation, t);
    setManque(m);
    if (m) return;
    const fini = { onSuccess: () => { toast(t('admin:calendrier.form.enregistre'), 'success'); onFermer(); } };
    if (creation) {
      creer.mutate(
        { ...regleEnvoyee(s), categoryCode: s.categoryCode, jurisdictionCode: s.jurisdictionCode, regimeCode: s.regimeCode || null },
        fini,
      );
    } else {
      modifier.mutate({ ...regleEnvoyee(s), id: ligne.id, version: ligne.version }, fini);
    }
  };

  const titre = creation
    ? t('admin:calendrier.form.titreCreation')
    : `${libelleDeClasse(ligne.categoryCode, t)} · ${nomDeJuridiction(ligne.jurisdictionCode, voc, langue)}`;

  return (
    <GsDrawer
      open
      onClose={onFermer}
      closeLabel={t('admin:calendrier.fermer')}
      title={titre}
      ariaLabel={titre}
      subtitle={creation ? t('admin:calendrier.form.sousTitreCreation') : t('admin:calendrier.form.sousTitreModification')}
      footer={
        <div className="flex justify-end gap-2">
          <button type="button" className={GS_COMPONENTS.btnSecondary} onClick={onFermer} disabled={enCours}>
            {t('admin:calendrier.annuler')}
          </button>
          <button type="button" className={GS_COMPONENTS.btnAction} onClick={enregistrer} disabled={enCours}>
            {enCours ? t('admin:calendrier.form.enregistrement') : t('admin:calendrier.form.enregistrer')}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {!creation && ligne.status === 'validee' && (
          <Bandeau ton="ambre" icone={AlertTriangle}>
            {t('admin:calendrier.form.avertissementValidee', { role: ligne.validatedByRole ?? '' })}
          </Bandeau>
        )}

        {creation && (
          <fieldset className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <legend className="sr-only">{t('admin:calendrier.form.cle')}</legend>
            <div className="sm:col-span-2">
              <label className={etiquette} htmlFor="cal-classe">{t('admin:calendrier.colonne.classe')}</label>
              <select id="cal-classe" className={champ} value={s.categoryCode} onChange={(e) => maj('categoryCode', e.target.value)}>
                <option value="">{t('admin:calendrier.form.choisir')}</option>
                {voc.classes.map((c) => (
                  <option key={c.code} value={c.code}>{libelleDeClasse(c.code, t, c.description)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={etiquette} htmlFor="cal-juridiction">{t('admin:calendrier.colonne.juridiction')}</label>
              <select
                id="cal-juridiction"
                className={champ}
                value={s.jurisdictionCode}
                onChange={(e) => setS((p) => ({ ...p, jurisdictionCode: e.target.value, regimeCode: '' }))}
              >
                <option value="">{t('admin:calendrier.form.choisir')}</option>
                {voc.jurisdictions.map((j) => (
                  <option key={j.code} value={j.code}>{langue.startsWith('en') ? j.nameEn : j.nameFr}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={etiquette} htmlFor="cal-regime">{t('admin:calendrier.form.regime')}</label>
              <select
                id="cal-regime"
                className={champ}
                value={s.regimeCode}
                disabled={regimes.length === 0}
                onChange={(e) => maj('regimeCode', e.target.value)}
              >
                <option value="">{t('admin:calendrier.form.tousRegimes')}</option>
                {regimes.map((r) => (
                  <option key={r.code} value={r.code}>{langue.startsWith('en') ? r.nameEn : r.nameFr}</option>
                ))}
              </select>
            </div>
          </fieldset>
        )}

        <fieldset className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <legend className="mb-2 text-field font-semibold text-slate-800 dark:text-slate-100">{t('admin:calendrier.form.regle')}</legend>
          <div className="sm:col-span-2">
            <Interrupteur
              checked={s.sansTerme}
              onChange={(v) => maj('sansTerme', v)}
              label={t('admin:calendrier.form.sansTerme')}
            />
          </div>
          {!s.sansTerme && (
            <div>
              <label className={etiquette} htmlFor="cal-duree">{t('admin:calendrier.form.dureeMois')}</label>
              <input
                id="cal-duree"
                className={champ}
                type="number"
                inputMode="numeric"
                min={1}
                max={1200}
                value={s.durationMonths}
                onChange={(e) => maj('durationMonths', e.target.value)}
              />
              {Number(s.durationMonths) >= 1 && Number.isInteger(Number(s.durationMonths)) && (
                <p className="mt-1 text-meta text-slate-500 dark:text-slate-400">{dureeLisible(Number(s.durationMonths), langue)}</p>
              )}
            </div>
          )}
          <div>
            <label className={etiquette} htmlFor="cal-declencheur">{t('admin:calendrier.colonne.declencheur')}</label>
            <select id="cal-declencheur" className={champ} value={s.triggerEvent} onChange={(e) => maj('triggerEvent', e.target.value)}>
              <option value="">{t('admin:calendrier.form.choisir')}</option>
              {voc.triggers.map((d) => (
                <option key={d} value={d}>{libelleDeDeclencheur(d, t)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={etiquette} htmlFor="cal-fin">{t('admin:calendrier.colonne.fin')}</label>
            <select
              id="cal-fin"
              className={champ}
              value={s.endAction}
              onChange={(e) => maj('endAction', e.target.value === 'anonymiser' ? 'anonymiser' : 'detruire')}
            >
              <option value="detruire">{libelleDeFin('detruire', t)}</option>
              <option value="anonymiser">{libelleDeFin('anonymiser', t)}</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className={etiquette} htmlFor="cal-base">{t('admin:calendrier.form.baseLegale')}</label>
            <textarea id="cal-base" className={champ} rows={2} maxLength={1000} value={s.legalBasis}
              onChange={(e) => maj('legalBasis', e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <label className={etiquette} htmlFor="cal-justification">{t('admin:calendrier.form.justification')}</label>
            <textarea id="cal-justification" className={champ} rows={2} maxLength={2000} value={s.rationale}
              onChange={(e) => maj('rationale', e.target.value)} />
          </div>
        </fieldset>

        <fieldset className="space-y-3">
          <legend className="mb-1 text-field font-semibold text-slate-800 dark:text-slate-100">{t('admin:calendrier.form.sources')}</legend>
          <p className="text-meta text-slate-500 dark:text-slate-400">{t('admin:calendrier.form.sourcesAide')}</p>
          {s.sources.map((src, i) => (
            <div key={i} className="space-y-2 rounded-xl border border-slate-200 p-3 dark:border-slate-700">
              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <label className={etiquette} htmlFor={`cal-src-titre-${i}`}>{t('admin:calendrier.form.sourceTitre')}</label>
                  <input id={`cal-src-titre-${i}`} className={champ} maxLength={500} value={src.title}
                    onChange={(e) => majSource(i, { title: e.target.value })} />
                </div>
                <button
                  type="button"
                  className={`${GS_COMPONENTS.btnIcon} mt-6 h-9 w-9 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700`}
                  aria-label={t('admin:calendrier.form.sourceRetirer')}
                  title={t('admin:calendrier.form.sourceRetirer')}
                  disabled={s.sources.length === 1}
                  onClick={() => setS((p) => ({ ...p, sources: p.sources.filter((_, j) => j !== i) }))}
                >
                  <X size={16} aria-hidden />
                </button>
              </div>
              <div>
                <label className={etiquette} htmlFor={`cal-src-url-${i}`}>{t('admin:calendrier.form.sourceAdresse')}</label>
                <input id={`cal-src-url-${i}`} className={champ} type="url" maxLength={1000}
                  value={src.url} onChange={(e) => majSource(i, { url: e.target.value })} />
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <div>
                  <label className={etiquette} htmlFor={`cal-src-date-${i}`}>{t('admin:calendrier.form.sourceConsultee')}</label>
                  <input id={`cal-src-date-${i}`} className={champ} type="date" value={src.consultedOn}
                    onChange={(e) => majSource(i, { consultedOn: e.target.value })} />
                </div>
                <div>
                  <label className={etiquette} htmlFor={`cal-src-nature-${i}`}>{t('admin:calendrier.form.sourceNature')}</label>
                  <GsSelect
                    value={src.nature}
                    ariaLabel={t('admin:calendrier.form.sourceNature')}
                    onChange={(v) => majSource(i, { nature: v === 'guide' || v === 'recherche' ? v : 'officielle' })}
                    options={(['officielle', 'guide', 'recherche'] as const).map((n) => ({ value: n, label: libelleDeNature(n, t) }))}
                  />
                </div>
              </div>
              <Interrupteur
                checked={src.draftedBefore2025}
                onChange={(v) => majSource(i, { draftedBefore2025: v })}
                label={t('admin:calendrier.form.sourceAncienne')}
              />
            </div>
          ))}
          <button type="button" className={`${GS_COMPONENTS.btnSecondary} inline-flex items-center gap-1.5`}
            onClick={() => setS((p) => ({ ...p, sources: [...p.sources, SOURCE_VIDE()] }))} disabled={s.sources.length >= 50}>
            <Plus size={16} aria-hidden />
            {t('admin:calendrier.form.sourceAjouter')}
          </button>
        </fieldset>

        <fieldset className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <legend className="mb-2 text-field font-semibold text-slate-800 dark:text-slate-100">{t('admin:calendrier.form.evenement')}</legend>
          <div>
            <label className={etiquette} htmlFor="cal-effet">{t('admin:calendrier.form.dateEffet')}</label>
            <input id="cal-effet" className={champ} type="date" value={s.effectiveDate}
              onChange={(e) => maj('effectiveDate', e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <label className={etiquette} htmlFor="cal-raison">{t('admin:calendrier.form.raison')}</label>
            <textarea id="cal-raison" className={champ} rows={2} maxLength={1000} value={s.reason}
              onChange={(e) => maj('reason', e.target.value)} />
          </div>
        </fieldset>

        {(manque || refus) && (
          <p role="alert" className="text-field font-semibold text-red-700 dark:text-red-400">
            {manque ?? libelleDeRefus(codeDuRefus(refus), t)}
          </p>
        )}
      </div>
    </GsDrawer>
  );
}

// ---------------------------------------------------------------------------
// Retrait et reprise : un evenement aussi, avec sa date et sa raison
// ---------------------------------------------------------------------------

function Bascule({ ligne, voc, onFermer }: { ligne: LigneDuCalendrier; voc: VocabulaireDuCalendrier; onFermer: () => void }) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const basculer = useBasculerLigne();
  const genre = ligne.isActive ? 'retrait' : 'reprise';
  const [effectiveDate, setEffectiveDate] = useState(aujourdhui());
  const [reason, setReason] = useState('');
  const [manque, setManque] = useState<string | null>(null);
  const titre = genre === 'retrait' ? t('admin:calendrier.bascule.titreRetrait') : t('admin:calendrier.bascule.titreReprise');

  const confirmer = () => {
    if (!reason.trim() || !effectiveDate) {
      setManque(t('admin:calendrier.form.manqueRaison'));
      return;
    }
    setManque(null);
    basculer.mutate(
      { id: ligne.id, genre, version: ligne.version, effectiveDate, reason: reason.trim() },
      { onSuccess: () => { toast(t('admin:calendrier.form.enregistre'), 'success'); onFermer(); } },
    );
  };

  return (
    <GsDrawer
      open
      onClose={onFermer}
      closeLabel={t('admin:calendrier.fermer')}
      title={titre}
      ariaLabel={titre}
      subtitle={`${libelleDeClasse(ligne.categoryCode, t)} · ${nomDeJuridiction(ligne.jurisdictionCode, voc, i18n.language)}`}
      footer={
        <div className="flex justify-end gap-2">
          <button type="button" className={GS_COMPONENTS.btnSecondary} onClick={onFermer} disabled={basculer.isPending}>
            {t('admin:calendrier.annuler')}
          </button>
          <button
            type="button"
            className={genre === 'retrait' ? GS_COMPONENTS.btnDangerOutline : GS_COMPONENTS.btnAction}
            onClick={confirmer}
            disabled={basculer.isPending}
          >
            {genre === 'retrait' ? t('admin:calendrier.bascule.retirer') : t('admin:calendrier.bascule.reprendre')}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <Bandeau ton={genre === 'retrait' ? 'ambre' : 'bleu'} icone={genre === 'retrait' ? AlertTriangle : Info}>
          {genre === 'retrait' ? t('admin:calendrier.bascule.aideRetrait') : t('admin:calendrier.bascule.aideReprise')}
        </Bandeau>
        <div>
          <label className={etiquette} htmlFor="cal-bascule-effet">{t('admin:calendrier.form.dateEffet')}</label>
          <input id="cal-bascule-effet" className={champ} type="date" value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)} />
        </div>
        <div>
          <label className={etiquette} htmlFor="cal-bascule-raison">{t('admin:calendrier.form.raison')}</label>
          <textarea id="cal-bascule-raison" className={champ} rows={3} maxLength={1000} value={reason}
            onChange={(e) => setReason(e.target.value)} />
        </div>
        {(manque || basculer.error) && (
          <p role="alert" className="text-field font-semibold text-red-700 dark:text-red-400">
            {manque ?? libelleDeRefus(codeDuRefus(basculer.error), t)}
          </p>
        )}
      </div>
    </GsDrawer>
  );
}

// ---------------------------------------------------------------------------
// L historique : le journal de la ligne, du plus recent au plus ancien
// ---------------------------------------------------------------------------

function dureeDuJournal(etat: Record<string, unknown> | null, langue: string, t: TFunction): string {
  if (!etat) return '—';
  const mois = etat.duration_months;
  return typeof mois === 'number' ? dureeLisible(mois, langue) : t('admin:calendrier.sansTerme');
}

function Historique({ ligne, voc, onFermer }: { ligne: LigneDuCalendrier; voc: VocabulaireDuCalendrier; onFermer: () => void }) {
  const { t, i18n } = useTranslation();
  const langue = i18n.language;
  const requete = useChangementsDuCalendrier(ligne.id);
  const titre = t('admin:calendrier.historique.titre');

  return (
    <GsDrawer
      open
      onClose={onFermer}
      closeLabel={t('admin:calendrier.fermer')}
      title={titre}
      ariaLabel={titre}
      subtitle={`${libelleDeClasse(ligne.categoryCode, t)} · ${nomDeJuridiction(ligne.jurisdictionCode, voc, langue)}`}
    >
      {requete.isLoading ? (
        <p className="text-field text-slate-500 dark:text-slate-400">{t('admin:calendrier.chargement')}</p>
      ) : requete.isError || !requete.data ? (
        <QueryError message={t('admin:calendrier.historique.erreur')} onRetry={() => void requete.refetch()} />
      ) : (
        <ol className="space-y-3">
          {requete.data.changements.map((c) => (
            <li key={c.id} className="rounded-xl border border-slate-200 bg-white p-3 shadow-xs dark:border-slate-700 dark:bg-slate-800">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-field font-semibold text-slate-800 dark:text-slate-100">{libelleDeChangement(c.changeKind, t)}</span>
                <span className="text-meta text-slate-500 dark:text-slate-400">
                  {t('admin:calendrier.historique.effet', { date: dateLisible(c.effectiveDate, langue) })}
                </span>
              </div>
              {(c.changeKind === 'creation' || c.changeKind === 'modification') && (
                <p className="mt-1 text-field text-slate-700 dark:text-slate-200">
                  {c.before === null
                    ? dureeDuJournal(c.after, langue, t)
                    : t('admin:calendrier.historique.passage', {
                        avant: dureeDuJournal(c.before, langue, t),
                        apres: dureeDuJournal(c.after, langue, t),
                      })}
                </p>
              )}
              <p className="mt-1 text-field text-slate-600 dark:text-slate-300">{c.reason}</p>
              <p className="mt-1 text-meta text-slate-400 dark:text-slate-500">
                {t('admin:calendrier.historique.inscrit', { date: dateLisible(c.changedAt, langue) })}
              </p>
            </li>
          ))}
        </ol>
      )}
    </GsDrawer>
  );
}

// ---------------------------------------------------------------------------
// La page
// ---------------------------------------------------------------------------

function nomDeProvinceEnAttente(code: string, t: TFunction): string {
  switch (code) {
    case 'CA-ON': return t('admin:calendrier.redige.province.ontario');
    case 'CA-AB': return t('admin:calendrier.redige.province.alberta');
    case 'CA-BC': return t('admin:calendrier.redige.province.colombieBritannique');
    default: return code;
  }
}

/**
 * Le calendrier redige par l agent (2026-10-07) : ce qu il ajouterait, et le bouton qui le
 * charge. Chaque ligne entre « redigee », avec son evenement date ; aucune ligne deja au
 * calendrier n est touchee. Les lignes d une province pas encore ouverte attendent.
 */
function CalendrierRedige({ etat }: { etat: EtatDuCalendrierRedige }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const charger = usePrechargerLeCalendrierRedige();
  const enAttente = etat.enAttenteDeProvince
    .map((p) => t('admin:calendrier.redige.uneProvince', { province: nomDeProvinceEnAttente(p.jurisdictionCode, t), n: p.lignes }))
    .join(', ');

  if (etat.aCharger === 0 && etat.enAttenteDeProvince.length === 0) return null;

  const lancer = () => {
    charger.mutate(aujourdhui(), {
      onSuccess: (bilan) => {
        toast(t('admin:calendrier.redige.bilan', { n: bilan.ajoutees }), 'success');
      },
    });
  };

  return (
    <Bandeau ton="bleu" icone={FileText}>
      {etat.aCharger > 0 ? (
        <>
          <p className="font-semibold">{t('admin:calendrier.redige.titre', { n: etat.aCharger })}</p>
          <p className="mt-1">{t('admin:calendrier.redige.aide')}</p>
        </>
      ) : (
        <p>{t('admin:calendrier.redige.toutCharge')}</p>
      )}
      {enAttente !== '' && <p className="mt-1">{t('admin:calendrier.redige.enAttente', { provinces: enAttente })}</p>}
      {etat.aCharger > 0 && (
        <button type="button" className={`${GS_COMPONENTS.btnAction} mt-3 inline-flex items-center gap-1.5 touch-manipulation`}
          disabled={charger.isPending} onClick={lancer}>
          <Download size={16} aria-hidden />
          {charger.isPending ? t('admin:calendrier.redige.enCours') : t('admin:calendrier.redige.bouton', { n: etat.aCharger })}
        </button>
      )}
      {charger.error && (
        <p role="alert" className="mt-2 font-semibold text-red-700 dark:text-red-400">
          {libelleDeRefus(codeDuRefus(charger.error), t)}
        </p>
      )}
    </Bandeau>
  );
}

type Ouvert =
  | { quoi: 'creation' }
  | { quoi: 'modification'; ligne: LigneDuCalendrier }
  | { quoi: 'bascule'; ligne: LigneDuCalendrier }
  | { quoi: 'historique'; ligne: LigneDuCalendrier };

export default function CalendrierConservation() {
  const { t, i18n } = useTranslation();
  const langue = i18n.language;
  const requete = useCalendrierConservation();
  const [ouvert, setOuvert] = useState<Ouvert | null>(null);
  const [voirRetirees, setVoirRetirees] = useState(false);

  const lignes = useMemo(
    () => (requete.data?.lignes ?? []).filter((l) => voirRetirees || l.isActive),
    [requete.data, voirRetirees],
  );

  if (requete.isLoading) {
    return <p className="p-6 text-field text-slate-500 dark:text-slate-400">{t('admin:calendrier.chargement')}</p>;
  }
  if (requete.error instanceof ApiForbiddenError) {
    return (
      <div className="mx-auto max-w-lg py-12 text-center">
        <Shield size={40} className="mx-auto mb-4 text-slate-300 dark:text-slate-600" aria-hidden />
        <p className="text-body font-semibold text-slate-800 dark:text-slate-100">{t('admin:calendrier.refuseT')}</p>
        <p className="mt-1 text-field text-slate-500 dark:text-slate-400">{t('admin:calendrier.refuseS')}</p>
      </div>
    );
  }
  if (requete.isError || !requete.data) {
    return <QueryError message={t('admin:calendrier.erreur')} onRetry={() => void requete.refetch()} />;
  }

  const voc = requete.data.vocabulaire;
  const nbSansTerme = requete.data.lignes.filter((l) => l.isActive && l.durationMonths === null).length;

  const actionsDeLigne = (l: LigneDuCalendrier) => (
    <div className="flex justify-end gap-1">
      {l.isActive && (
        <button type="button" className={`${GS_COMPONENTS.btnIcon} h-8 w-8 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700`}
          aria-label={t('admin:calendrier.modifier')} title={t('admin:calendrier.modifier')}
          onClick={(e) => { e.stopPropagation(); setOuvert({ quoi: 'modification', ligne: l }); }}>
          <Pencil size={16} aria-hidden />
        </button>
      )}
      <button type="button" className={`${GS_COMPONENTS.btnIcon} h-8 w-8 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700`}
        aria-label={t('admin:calendrier.historique.titre')} title={t('admin:calendrier.historique.titre')}
        onClick={(e) => { e.stopPropagation(); setOuvert({ quoi: 'historique', ligne: l }); }}>
        <History size={16} aria-hidden />
      </button>
      <button type="button" className={`${GS_COMPONENTS.btnIcon} h-8 w-8 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700`}
        aria-label={l.isActive ? t('admin:calendrier.bascule.retirer') : t('admin:calendrier.bascule.reprendre')}
        title={l.isActive ? t('admin:calendrier.bascule.retirer') : t('admin:calendrier.bascule.reprendre')}
        onClick={(e) => { e.stopPropagation(); setOuvert({ quoi: 'bascule', ligne: l }); }}>
        {l.isActive ? <Trash2 size={16} aria-hidden /> : <RotateCcw size={16} aria-hidden />}
      </button>
    </div>
  );

  const colonnes: Column<LigneDuCalendrier>[] = [
    {
      key: 'classe',
      header: t('admin:calendrier.colonne.classe'),
      render: (l) => (
        <span className="font-semibold text-slate-800 dark:text-slate-100">
          {libelleDeClasse(l.categoryCode, t, voc.classes.find((c) => c.code === l.categoryCode)?.description)}
        </span>
      ),
    },
    {
      key: 'juridiction',
      header: t('admin:calendrier.colonne.juridiction'),
      render: (l) => (
        <span>
          {nomDeJuridiction(l.jurisdictionCode, voc, langue)}
          {l.regimeCode && (
            <span className="block text-meta text-slate-500 dark:text-slate-400">{nomDeRegime(l.regimeCode, voc, langue)}</span>
          )}
        </span>
      ),
    },
    { key: 'duree', header: t('admin:calendrier.colonne.duree'), render: (l) => <Duree mois={l.durationMonths} langue={langue} t={t} /> },
    { key: 'declencheur', header: t('admin:calendrier.colonne.declencheur'), render: (l) => libelleDeDeclencheur(l.triggerEvent, t) },
    { key: 'fin', header: t('admin:calendrier.colonne.fin'), render: (l) => libelleDeFin(l.endAction, t) },
    { key: 'statut', header: t('admin:calendrier.colonne.statut'), render: (l) => <Statut ligne={l} t={t} /> },
    { key: 'effet', header: t('admin:calendrier.colonne.effet'), render: (l) => dateLisible(l.effectiveDate, langue) },
    { key: 'actions', header: '', render: actionsDeLigne, headerClassName: 'sr-only' },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('admin:calendrier.titre')}
        description={t('admin:calendrier.sousTitre')}
        actions={
          <button type="button" className={`${GS_COMPONENTS.btnAction} inline-flex items-center gap-1.5`}
            onClick={() => setOuvert({ quoi: 'creation' })}>
            <Plus size={16} aria-hidden />
            {t('admin:calendrier.ajouter')}
          </button>
        }
      />

      <Bandeau ton="bleu" icone={CalendarClock}>
        <p>{t('admin:calendrier.aide.statuts')}</p>
        <p className="mt-1">{t('admin:calendrier.aide.sansTerme')}</p>
      </Bandeau>

      {requete.data.redige && <CalendrierRedige etat={requete.data.redige} />}

      {nbSansTerme > 0 && (
        <Bandeau ton="ambre" icone={AlertTriangle}>
          {t('admin:calendrier.aide.aFixer', { n: nbSansTerme })}
        </Bandeau>
      )}

      <section className="rounded-xl bg-white shadow-xs dark:bg-slate-800">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 p-4 dark:border-slate-700">
          <h2 className="text-head font-semibold text-slate-800 dark:text-slate-100">
            {t('admin:calendrier.lignes', { n: lignes.length })}
          </h2>
          <Interrupteur checked={voirRetirees} onChange={setVoirRetirees} label={t('admin:calendrier.voirRetirees')} />
        </div>
        <DataTable
          columns={colonnes}
          data={lignes}
          rowKey={(l) => l.id}
          emptyMessage={t('admin:calendrier.vide')}
          onRowClick={(l) => setOuvert({ quoi: l.isActive ? 'modification' : 'historique', ligne: l })}
        />
      </section>

      {ouvert?.quoi === 'creation' && <Formulaire ligne={null} voc={voc} onFermer={() => setOuvert(null)} />}
      {ouvert?.quoi === 'modification' && <Formulaire ligne={ouvert.ligne} voc={voc} onFermer={() => setOuvert(null)} />}
      {ouvert?.quoi === 'bascule' && <Bascule ligne={ouvert.ligne} voc={voc} onFermer={() => setOuvert(null)} />}
      {ouvert?.quoi === 'historique' && <Historique ligne={ouvert.ligne} voc={voc} onFermer={() => setOuvert(null)} />}
    </div>
  );
}
